// Печатные формы: счёт на оплату, договор-счёт, карточка договора, КП, акт работ.
//
// ЗАЧЕМ РАЗДЕЛ. Сегодня печатает Bitrix — его documentgenerator дёргают 36 мест в
// бизнес-процессах. Через месяц Bitrix отключат, и печатать станет нечем. Здесь то же
// самое собирается из СВОЕЙ базы.
//
// ЧЕГО ЭТОТ ЭКРАН НЕ ДЕЛАЕТ. Он не решает, годится ли документ к печати. Решает ядро
// (print_forms.py), и экран показывает его ответ дословно: отказы — списком с причинами,
// оговорки — списком, лист — окном печати. Заведи мы тут «свою» проверку, у формы стало
// бы два мнения о том, можно ли печатать, и однажды они разошлись бы.
//
// ПОЧЕМУ ОТКАЗ ВЫГЛЯДИТ КАК ОТВЕТ, А НЕ КАК ОШИБКА. «Печатать нельзя, потому что у
// плательщика нет ИНН» — это результат работы, а не поломка. Красная плашка «ошибка»
// заставила бы человека идти чинить систему вместо карточки контрагента.
import { useState } from 'react'

import type { ОписаниеФормы, ОтветФормы } from '../api/printForms'
import { собратьЛист, useСправкаПечати } from '../api/printForms'
import { QueryState } from '../components/QueryState'
import { plural, pluralN } from '../lib/format'
import { открытьЛист } from '../lib/print73'

/** Сколько строк в источнике формы — по имени таблицы из ответа ядра. */
const СЧЁТЧИК_ИСТОЧНИКА: Record<string, string> = {
  invoice: 'invoices',
  contract: 'contracts',
  deal: 'deals_with_lines',
  act: 'acts',
}

const ИМЯ_ИСТОЧНИКА: Record<string, [string, string, string]> = {
  invoice: ['счёт', 'счёта', 'счетов'],
  contract: ['договор', 'договора', 'договоров'],
  deal: ['сделка с составом', 'сделки с составом', 'сделок с составом'],
  act: ['акт', 'акта', 'актов'],
}

export default function PrintForms() {
  const справка = useСправкаПечати()
  const [форма, setФорма] = useState('')
  const [номер, setНомер] = useState('')
  const [ответ, setОтвет] = useState<ОтветФормы | null>(null)
  const [беда, setБеда] = useState('')
  const [идёт, setИдёт] = useState(false)

  const формы = справка.data?.forms ?? []
  const выбранная: ОписаниеФормы | undefined =
    формы.find((ф) => ф.form === форма) ?? формы[0]
  const счётчики = справка.data?.counts ?? {}
  const строкВИсточнике = выбранная
    ? счётчики[СЧЁТЧИК_ИСТОЧНИКА[выбранная.source] ?? ''] ?? 0
    : 0

  const собрать = async () => {
    if (!выбранная) return
    setБеда('')
    setОтвет(null)
    const ид = Number(номер)
    if (!Number.isInteger(ид) || ид <= 0) {
      setБеда('Номер записи — целое число больше нуля.')
      return
    }
    setИдёт(true)
    try {
      const о = await собратьЛист(выбранная, ид)
      setОтвет(о)
      // Лист открывается сразу: человек нажал «Собрать лист», а не «показать отказы».
      // Если печатать нельзя — окно не открывается вовсе, и причины он видит ниже.
      if (о.ok && о.html && открытьЛист(о.html) === 'blocked') {
        setБеда('Браузер не дал открыть окно печати. Разрешите всплывающие окна для'
          + ' этого адреса и нажмите ещё раз — лист открывается отдельной страницей.')
      }
    } catch (e) {
      setБеда((e as Error).message)
    } finally {
      setИдёт(false)
    }
  }

  const открытьЕщёРаз = () => {
    if (ответ?.html && открытьЛист(ответ.html) === 'blocked') {
      setБеда('Браузер не дал открыть окно печати.')
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Печатные формы</h1>
        <span className="spacer" />
        <span className="hint">
          Листы собираются из своей базы. Ничего никуда не записывается.
        </span>
      </div>

      <QueryState isLoading={справка.isLoading} error={справка.error}>
        {справка.data && (
          <>
            <div className="card">
              <h2>Чем можно печатать сегодня</h2>
              <div className="hint">
                Источник: {справка.data.source}. Числа ниже посчитаны запросом в момент
                открытия страницы, а не записаны в подпись.
              </div>
              <div className="table-wrap cards">
                <table>
                  <thead>
                    <tr>
                      <th>Форма</th>
                      <th>Источник</th>
                      <th className="num">Записей</th>
                    </tr>
                  </thead>
                  <tbody>
                    {справка.data.forms.map((ф) => {
                      const n = счётчики[СЧЁТЧИК_ИСТОЧНИКА[ф.source] ?? ''] ?? 0
                      const слова = ИМЯ_ИСТОЧНИКА[ф.source]
                      return (
                        <tr key={ф.form}>
                          <td data-label="Форма">{ф.title}</td>
                          <td data-label="Источник">
                            {ф.needs}
                            {слова && n > 0 && (
                              <span className="hint"> — {plural(n, ...слова)}</span>
                            )}
                          </td>
                          <td data-label="Записей" className="num">{n}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <h2 style={{ marginTop: 16 }}>Наши юрлица</h2>
              <div className="hint">
                Счёт на оплату печатается только от юрлица с годным расчётным счётом:
                в реквизите нужны имя банка, девятизначный БИК и двадцатизначный счёт,
                начинающийся на 407 или 408. Корреспондентский счёт на месте расчётного
                формой не принимается — в шапке счёта «Сч. №» стоит дважды, и платёж по
                перепутанным строкам уходит в никуда.
              </div>
              {справка.data.own_companies.length === 0 ? (
                <div className="empty">
                  <div className="empty-title">Наших юрлиц в базе нет</div>
                  <div className="empty-hint">
                    Признак «наша компания» ставится в карточке компании. Без него
                    печатать не от кого.
                  </div>
                </div>
              ) : (
                <div className="table-wrap cards">
                  <table>
                    <thead>
                      <tr>
                        <th>Юрлицо</th>
                        <th>ИНН</th>
                        <th>КПП</th>
                        <th>Расчётный счёт</th>
                        <th className="num">Адресов</th>
                      </tr>
                    </thead>
                    <tbody>
                      {справка.data.own_companies.map((ю) => (
                        <tr key={ю.id}>
                          <td data-label="Юрлицо">{ю.name ?? `#${ю.id}`}</td>
                          <td data-label="ИНН" className="num">{ю.inn ?? '—'}</td>
                          <td data-label="КПП" className="num">{ю.kpp ?? '—'}</td>
                          <td data-label="Расчётный счёт">
                            {ю.bank_ok
                              ? (ю.bank_name ?? 'есть')
                              : <span className="error">нет годного</span>}
                          </td>
                          <td data-label="Адресов" className="num">{ю.addresses}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {Object.entries(справка.data.data_notes).map(([ключ, текст]) => (
                <div className="hint" key={ключ}>{текст}</div>
              ))}
            </div>

            <div className="card">
              <h2>Собрать лист</h2>
              <div className="hint">
                Номер — это номер записи в CRM (счёта, договора, сделки или акта), а не
                номер документа на бумаге.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <select
                  aria-label="Печатная форма"
                  value={выбранная?.form ?? ''}
                  onChange={(e) => { setФорма(e.target.value); setОтвет(null) }}
                >
                  {формы.map((ф) => (
                    <option key={ф.form} value={ф.form}>{ф.title}</option>
                  ))}
                </select>
                <input
                  aria-label="Номер записи"
                  inputMode="numeric"
                  value={номер}
                  placeholder="номер записи"
                  onChange={(e) => setНомер(e.target.value.replace(/\D/g, ''))}
                />
                <button type="button" onClick={собрать} disabled={идёт || !выбранная}>
                  {идёт ? 'Собираю…' : 'Собрать лист'}
                </button>
              </div>
              {выбранная && строкВИсточнике === 0 && (
                <div className="empty" style={{ marginTop: 12 }}>
                  <div className="empty-title">
                    Записей для формы «{выбранная.title}» в базе пока нет
                  </div>
                  <div className="empty-hint">
                    Нужно: {выбранная.needs}. Перенос ещё не дошёл до этой таблицы —
                    печатать нечего, и пустой бланк вместо документа мы не выдаём.
                  </div>
                </div>
              )}
              {беда && <div className="notice err">{беда}</div>}
            </div>

            {ответ && <Итог ответ={ответ} открыть={открытьЕщёРаз} />}
          </>
        )}
      </QueryState>
    </>
  )
}

function Итог({ ответ, открыть }: { ответ: ОтветФормы; открыть: () => void }) {
  return (
    <div className="card">
      <h2>{ответ.ok ? 'Лист собран' : 'Печатать нельзя'}</h2>
      {ответ.ok ? (
        <>
          <div className="hint">
            Лист открыт отдельным окном. PDF делается печатью браузера: Ctrl+P →
            «Сохранить как PDF».
          </div>
          <button type="button" onClick={открыть}>Открыть лист ещё раз</button>
        </>
      ) : (
        <div className="notice err">
          <b>
            {pluralN(ответ.refusals.length, 'причина', 'причины', 'причин')}, по которым
            документ не выпущен
          </b>
          <ul>
            {ответ.refusals.map((о) => (
              <li key={о.code}>{о.text}</li>
            ))}
          </ul>
          {ответ.hint && <div className="hint">{ответ.hint}</div>}
        </div>
      )}
      {ответ.warnings.length > 0 && (
        <div className="notice">
          <b>
            {pluralN(ответ.warnings.length, 'оговорка', 'оговорки', 'оговорок')} к листу
          </b>
          <ul>
            {ответ.warnings.map((о) => (
              <li key={о.code}>{о.text}</li>
            ))}
          </ul>
          {ответ.ok && (
            <div className="hint">
              Эти же оговорки напечатаны на самом листе: он уезжает от экрана, и на
              бумаге они нужнее, чем здесь.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
